import {
  siteSessionAccessTokenKeyName,
  siteSessionExpiresAt,
  siteSessionIdTokenKeyName,
  siteSessionRefresh,
  siteSessionTokenKeyName,
} from "@/config/environment";
import { routes } from "@/config/routing/routes";
import { removeCookie, setCookie } from "@/tools/cookies";

interface SignInRedirectPathOptions {
  hash?: string;
  pathname?: string;
  preserveReturnTo?: boolean;
  search?: string;
}

const authPaths = new Set([
  routes.signIn.absolute,
  routes.resetPassword.absolute,
  routes.sendResetPassword.absolute,
  routes.authCallback.absolute,
]);

export const clearAuthSession = () => {
  clearAuthCookies();

  if (typeof window !== "undefined") {
    const storage = window.localStorage;

    if (typeof storage?.clear === "function") {
      storage.clear();
    }
  }
};

export const clearAuthCookies = () => {
  removeCookie(siteSessionTokenKeyName);
  removeCookie(siteSessionRefresh);
  removeCookie(siteSessionIdTokenKeyName);
  removeCookie(siteSessionAccessTokenKeyName);
  removeCookie(siteSessionExpiresAt);
};

const getNext5amTimestamp = (): number => {
  const next5am = new Date();
  next5am.setHours(5, 0, 0, 0);
  if (next5am.getTime() <= Date.now()) {
    next5am.setDate(next5am.getDate() + 1);
  }
  return next5am.getTime();
};

export const setSessionExpiry = () => {
  const expiresAt = getNext5amTimestamp();
  setCookie(siteSessionExpiresAt, String(expiresAt), {
    expires: new Date(expiresAt),
  });
};

export const getSignInRedirectPath = ({
  hash = "",
  pathname = routes.signIn.absolute,
  preserveReturnTo = true,
  search = "",
}: SignInRedirectPathOptions = {}) => {
  if (!preserveReturnTo || authPaths.has(pathname)) {
    return routes.signIn.absolute;
  }

  const returnTo = `${pathname}${search}${hash}`;

  return `${routes.signIn.absolute}?returnTo=${encodeURIComponent(returnTo)}`;
};

export const redirectToSignIn = ({
  preserveReturnTo = true,
}: Pick<SignInRedirectPathOptions, "preserveReturnTo"> = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const target = getSignInRedirectPath({
    hash: window.location.hash,
    pathname: window.location.pathname,
    preserveReturnTo,
    search: window.location.search,
  });
  const currentLocation = `${window.location.pathname}${window.location.search}`;

  if (currentLocation === target) {
    return;
  }

  window.location.replace(target);
};

export const resetSessionAndRedirectToSignIn = (
  options: Pick<SignInRedirectPathOptions, "preserveReturnTo"> = {},
) => {
  clearAuthSession();
  redirectToSignIn(options);
};
